import { useCallback, useRef } from "react";

/**
 * Hook that consumes SSE from the backend streaming endpoints.
 * Returns a function that takes a URL + body and yields parsed events via callbacks.
 */
export const useStreamingChat = () => {
  const abortRef = useRef(null);

  const streamMessage = useCallback(async (url, body, callbacks) => {
    const {
      onUserMessage,
      onAgentThinking,
      onAgentActivities,
      onTextChunk,
      onComplete,
      onError,
      onDone,
    } = callbacks;

    // Abort any existing stream
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);
            switch (event.type) {
              case "user_message":
                onUserMessage?.(event.data);
                break;
              case "agent_thinking":
                onAgentThinking?.(event.data);
                break;
              case "agent_activities":
                onAgentActivities?.(event.data);
                break;
              case "text_chunk":
                onTextChunk?.(event.data);
                break;
              case "complete":
                onComplete?.(event.data);
                break;
              case "error":
                onError?.(event.data);
                break;
              case "done":
                onDone?.();
                break;
              case "heartbeat":
                break;
              case "regeneration_started":
                break;
              default:
                break;
            }
          } catch {
            // skip malformed JSON
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        onError?.(err.message);
      }
    }
  }, []);

  const abort = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return { streamMessage, abort };
};
